import { useState, useRef } from "react";
import { Microphone, Stop, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

// Reusable voice-input button: records mic audio and returns a transcript via onResult(text).
export const MicButton = ({ onResult, size = 16, className = "", title = "Voice input" }) => {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice input not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState("processing");
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const { text } = await api.transcribe(blob);
          if (text) onResult?.(text);
          else toast.error("Didn't catch that — please try again.");
        } catch (err) {
          toast.error("Transcription failed", { description: err?.response?.data?.detail || err.message });
        } finally {
          setState("idle");
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setState("recording");
    } catch (err) {
      toast.error("Microphone unavailable", { description: "Allow microphone access to use voice input." });
    }
  };

  const stop = () => { if (recorderRef.current && state === "recording") recorderRef.current.stop(); };
  const toggle = () => (state === "recording" ? stop() : state === "idle" ? start() : null);

  return (
    <button
      type="button"
      data-testid="mic-button"
      onClick={toggle}
      title={state === "recording" ? "Stop and transcribe" : title}
      aria-label={title}
      className={`inline-flex items-center justify-center rounded-full transition-colors ${
        state === "recording" ? "text-red-600" : "text-muted-foreground hover:text-foreground"
      } ${className}`}
    >
      {state === "processing" ? (
        <CircleNotch size={size} className="animate-spin" />
      ) : state === "recording" ? (
        <Stop size={size} weight="fill" className="animate-pulse" />
      ) : (
        <Microphone size={size} />
      )}
    </button>
  );
};

export default MicButton;
